import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Grid from '@mui/material/Grid'
import lowerCase from 'lodash/lowerCase'
import startCase from 'lodash/startCase'
import React from 'react'

import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import LocationMapImageV2 from 'components/customer-details/content/location/LocationMapImageV2'
import { MARKER_TYPE } from 'components/customer-details/content/location/settings'
import HHTextField from 'components/form-fields/v5/HHTextField'
import T from 'T'
import { get } from 'utils/lodash'

import type { MaterialOption, MeasureOption, WorkOrderContainer } from './types'

const MAP_HEIGHT = 470

type ContainerDetailDialogProps = {
  open: boolean
  container: WorkOrderContainer | null
  measureKeyBy: Record<string, MeasureOption>
  materialKeyBy: Record<string, MaterialOption>
  onClose: () => void
}

// Read-only mirror of the CS AddEditContainerDialog.
const ContainerDetailDialog: React.FC<ContainerDetailDialogProps> = ({ open, container, measureKeyBy, materialKeyBy, onClose }) => {
  if (!container) return null

  const { serialNumber, status, latitude, longitude, measureId, materialId } = container
  const title = container.name || container.containerName || T.CONTAINER
  const containerType = get(container, 'containerType.name', '')
  const measureLabel = get(measureKeyBy, [measureId ?? '', 'value'], '')
  const materialLabel = get(materialKeyBy, [materialId ?? '', 'materialType'], '')
  const accountName = get(container, 'account.accountName', '')
  const locationName = get(container, 'location.locationName', '')
  const statusLabel = status ? startCase(lowerCase(status)) : ''

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <HHDialogTitle title={title} onClose={onClose} />
      <DialogContent>
        <Grid container columnSpacing={4} sx={{ width: '100%' }}>
          <Grid container flexDirection="column" rowSpacing={2} size={4}>
            <Grid size={12}>
              <HHTextField fullWidth readOnly label={T.CONTAINER_ID} value={title} />
            </Grid>
            {containerType && (
              <Grid size={12}>
                <HHTextField fullWidth readOnly label={T.CONTAINER_TYPE} value={containerType} />
              </Grid>
            )}
            <Grid size={12}>
              <HHTextField fullWidth readOnly label={T.SERIAL_NUMBER} value={serialNumber || T.NOT_SERIALIZED} />
            </Grid>
            {measureLabel && (
              <Grid size={12}>
                <HHTextField fullWidth readOnly label={T.MEASURE} value={measureLabel} />
              </Grid>
            )}
            {materialLabel && (
              <Grid size={12}>
                <HHTextField fullWidth readOnly label={T.MATERIAL} value={materialLabel} />
              </Grid>
            )}
            {statusLabel && (
              <Grid size={12}>
                <HHTextField fullWidth readOnly label={T.STATUS} value={statusLabel} />
              </Grid>
            )}
            {accountName && (
              <Grid size={12}>
                <HHTextField fullWidth readOnly label={T.ACCOUNT} value={accountName} />
              </Grid>
            )}
            {locationName && (
              <Grid size={12}>
                <HHTextField fullWidth readOnly label={T.LOCATION} value={locationName} />
              </Grid>
            )}
            {/* Nested row inside the column so lat/long share one line (saves vertical height). */}
            <Grid size={12}>
              <Grid container columnSpacing={2}>
                <Grid size={6}>
                  <HHTextField fullWidth readOnly label={T.LATITUDE} value={latitude != null ? String(latitude) : ''} />
                </Grid>
                <Grid size={6}>
                  <HHTextField fullWidth readOnly label={T.LONGITUDE} value={longitude != null ? String(longitude) : ''} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={8}>
            <Box height={MAP_HEIGHT}>
              <LocationMapImageV2
                dynamicWidth
                showPlaceholder
                containerData={container}
                containerId={`wo-container-detail-${container.id}`}
                height={MAP_HEIGHT}
                latitude={latitude}
                longitude={longitude}
                markerType={MARKER_TYPE.INVENTORY_CONTAINER}
                showInvalidAlert={false}
                width={600}
                zoom={20}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <HHDialogActions>
        <Button variant="outlined" onClick={onClose}>
          {T.CLOSE}
        </Button>
      </HHDialogActions>
    </Dialog>
  )
}

export default ContainerDetailDialog
